import {ICardM, IPileM} from "./MTypes";
import MField from "./MField";
import {Player} from "../../../../../../../types";

export default class MState {
    private playerId: string = '';
    private players: Array<Player> = [];

    private oldField: MField;
    private newField: MField;

    private anglMap = new Map<number, IPileM>();
    private flipMap = new Map<number, IPileM>();
    private lockMap = new Map<number, IPileM>();
    private moveMap = new Map<number, IPileM>();
    private usedMap = new Map<number, IPileM>();
    private shufMap = new Map<number, IPileM>();
    private sizeMap = new Map<number, IPileM>();

    private isDragFlip: boolean = false;

    constructor() {
        this.oldField = new MField;
        this.newField = new MField;
    }

    /**
     * информация об игроках
     */
    public setPlayers(playerId: string, players: Array<Player>): void
    {
        this.playerId = playerId ?? '';
        this.players = players ?? [];
    }

    /**
     * признак "флип на лету"
     */
    public setDragFlip(): void
    {
        this.isDragFlip = true;
    }

    public getDragFlip(): boolean
    {
        return this.isDragFlip;
    }

    /**
     * обновить данные
     */
    public update(gameField: any): void
    {
        this.newField.update(gameField, this.playerId);
    }

    /**
     * получить пачку по ID
     */
    public getPile(id: number): IPileM | undefined
    {
        return this.newField.getPile(id);
    }

    public getCardIndex(id: number): number
    {
        return this.newField.getFirstCardIndex(id);
    }

    /**
     * пачка нуждается в обновлении
     */
    private isDirty(id: number): boolean
    {
        const oldPile = this.oldField.getPile(id);
        const newPile = this.newField.getPile(id);

        let dirty = false;
        if (oldPile && newPile) {
            dirty = oldPile.cards.length !== newPile.cards.length;

            if (oldPile.angle !== newPile.angle) {
                this.anglMap.set(id, newPile);
                dirty = true;
            }

            if (oldPile.flipped !== newPile.flipped) {
                this.flipMap.set(id, newPile);
                dirty = true;
            }

            if (oldPile.locked !== newPile.locked) {
                this.lockMap.set(id, newPile);
                dirty = true;
            }

            if (oldPile.usedByPlayer !== newPile.usedByPlayer || oldPile.blockedByPlayer !== newPile.blockedByPlayer) {
                this.usedMap.set(id, newPile);
                dirty = true;
            }

            if (oldPile.x !== newPile.x || oldPile.y !== newPile.y) {
                this.moveMap.set(id, newPile);
                dirty = true;
            }

            if (newPile.shuffled) {
                this.shufMap.set(id, newPile);
                dirty = true;
                newPile.shuffled = false;
            }

            if (oldPile.cards.length !== newPile.cards.length) {
                this.sizeMap.set(id, newPile);
                dirty = true;
            }

        }
        return dirty;
    }

    /**
     * получить дифф
     */
    public get diff(): {
        pilesCreate: Array<IPileM>,
        pilesDelete: Array<IPileM>,
        pilesUpdate: Array<IPileM>,
        handCreate: Array<ICardM>
    } {
        this.anglMap.clear();
        this.flipMap.clear();
        this.lockMap.clear();
        this.moveMap.clear();
        this.usedMap.clear();
        this.shufMap.clear();
        this.sizeMap.clear();

        const oldHandIds = this.oldField.idHand;
        const newHandIds = this.newField.idHand;
        const handCreate = newHandIds.filter(id => !oldHandIds.includes(id));
        // const handDelete = oldHandIds.filter(id => !newHandIds.includes(id));
        // if (handCreate.length) {
        //     console.log('%c ### HAND Create:', 'color: #f0f', handCreate);
        // }
        // if (handDelete.length) {
        //     console.log('%c ### HAND Delete:', 'color: #f0f', handDelete);
        // }

        const oldIds = this.oldField.idList;
        const newIds = this.newField.idList;

        const listCreate = newIds.filter(id => !oldIds.includes(id));
        const listDelete = oldIds.filter(id => !newIds.includes(id));
        const listUpdate = newIds.filter(id => oldIds.includes(id)).filter(id => this.isDirty(id));
        const diffPiles = {
            handCreate: this.newField.selectHand(handCreate),
            pilesCreate: this.newField.selectPiles(listCreate),
            pilesDelete: this.oldField.selectPiles(listDelete),
            pilesUpdate: this.newField.selectPiles(listUpdate),
        };
        this.oldField.overwrite(this.newField);
        return diffPiles;
    }

    /**
     * получить rotate list
     */
    public get angleList(): Array<{ id: number, angle: number }>
    {
        return Array.from(this.anglMap.values())
            .map(pile => ({ id: pile.id, angle: pile.angle ?? 0 }));
    }

    /**
     * получить flip list
     */
    public get flipList(): Array<{ id: number, flipped: boolean }>
    {
        this.isDragFlip = false;
        return Array.from(this.flipMap.values())
            .map(pile => ({ id: pile.id, flipped: (pile.flipped ?? false) }));
    }
    // public get flipList(): Array<number>
    // {
    //     this.isDragFlip = false;
    //     return Array.from(this.flipMap.keys());
    // }

    /**
     * получить lock list
     */
    public get lockList(): Array<{ id: number, locked: boolean }>
    {
        return Array.from(this.lockMap.values())
            .map(pile => ({ id: pile.id, locked: pile.locked }));
    }

    /**
     * получить tryMove list
     */
    public get moveList(): Array<{ id: number, x: number, y: number }>
    {
        return Array.from(this.moveMap.values())
            .map(pile => ({ id: pile.id, x: pile.x, y: pile.y }));
    }

    /**
     * получить used list
     */
    public get usedList(): Array<{ id: number, usedByPlayer: string, blockedByPlayer: string }>
    {
        return Array.from(this.usedMap.values())
            .map(pile => ({ id: pile.id, usedByPlayer: pile.usedByPlayer, blockedByPlayer: pile.blockedByPlayer }));
    }

    /**
     * получить shuffled list
     */
    public get shuffledList(): Array<number>
    {
        return Array.from(this.shufMap.keys());
    }

    /**
     * получить size list
     */
    public get sizeList(): Array<IPileM>
    {
        return Array.from(this.sizeMap.values());
    }

    /**
     * Обработка диффов
     */
    public diffHandler(it:any): void
    {
        switch (it.operation) {
            case 'addStack':
                this.newField.actionAddStack(it.value);
                break;
            /**
             * Добавить стопку со стола в руку
             */
            case 'addCards':
                this.newField.actionAddCardsToHand(it.value);
                break;
            /**
             * Добавить карты в стопку
             */
            case 'addStackCards':
                this.newField.actionAddCardsToStack(it.value);
                break;
            /**
             * Удалить карты из стопки
             */
            case 'deleteStackCards':
                this.newField.actionRemoveCardsFromStack(it.value);
                break;
            case 'deleteCards':
                this.newField.actionDeleteHandCard(it.value.ids);
                break;
            case 'deleteStacks':
                this.newField.actionDeleteStacks(it.value.ids);
                break;
            case 'disableStacks':
                this.newField.actionDisableStacks(it.value);
                break;
            case 'enableStacks':
                this.newField.actionEnableStacks(it.value);
                break;
            case 'flipStack':
                this.newField.actionFlipPile(it.value);
                break;
            case 'moveStack':
                this.newField.actionMovePile(it.value);
                break;
            case 'rotateStack':
                this.newField.actionRotatePile(it.value);
                break;
            case 'shuffleStack':
                this.newField.actionShufflePile(it.value);
                break;
            case 'unlockStack':
                this.newField.actionLockPile(it.value.id, false);
                break;
            case 'lockStack':
                setTimeout(() => {this.newField.actionLockPile(it.value.id, true);}, 0);
                break;
            default:
                console.warn('unknown operation: ' + it.operation);
        }
    }

}
